import * as React from 'react';
import { useCallback, useState, useMemo } from 'react';
import { css } from '@emotion/core';
import { Str } from '../../localization/translationKeys';
import { useMsgText } from '../../localization/hooks';
import ModalHeader from '../../layout/modal/ModalHeader';
import LocalDiv from '../../localization/LocalDiv';
import Modal from '../../layout/modal/Modal';
import { FLEX_COLUMN, FLEX_ROW } from '../../layout/flexBox';
import { useHistory } from 'react-router-dom';
import LocalBtn from '../../localization/LocalBtn';
import Spacer from '../../layout/Spacer';
import { useRoutes } from '../../navigation/routes';
import { useAppState } from '../../core/redux/hooks';
import { useDispatch } from 'react-redux';
import { COLORS } from '../../globalStyle/colors';
import { btnStyle } from '../../forms/styles/button';
import { SELECT } from '../../forms/styles/select';
import { INPUT } from '../../forms/styles/input';
import { addDevPlanItemAction } from './api/addDevPlanItemAction';
import Msg from '../../localization/Msg';


const styles = {
  modal:       [
    FLEX_COLUMN,
    css`
      min-height: 0;
    `,
  ],
  root:        [
    FLEX_COLUMN,
    css`
      transition: opacity .5s ease;
      justify-content: space-between;
    `,
  ],
  formInputs:  [
    FLEX_COLUMN,
    css`
      align-items: stretch;
      margin: 0 40px;
    `,
  ],
  loadingForm: css`
    opacity: .4;
  `,
  radioBtn:    css`
    width: 18px;
    height: 18px;
    flex-basis: 18px;
    flex-shrink: 0;
    flex-grow: 0;
  `,
  inputGroup:  [
    FLEX_COLUMN,
    css`
      align-items: stretch;
      margin-bottom: 35px;
    `,
  ],
  label:       [
    FLEX_ROW,
    css`
      transition: opacity .5s ease;
      align-items: center;
      color: ${COLORS.DARK_GREY};
      font-size: 12px;
    `,
  ],
  select:      [
    SELECT,
    css`
      flex-grow: 1;
      flex-shrink: 1;
      flex-basis: 320px;
      min-width: 0;
      max-width: 320px;
    `,
  ],
  orMsg:       css`
    margin: 0 50px; /* weird margin is to align this text with the text boxes... */
  `,
  txtBox:      [
    INPUT(),
    css`
      flex-grow: 1;
      flex-shrink: 1;
      flex-basis: 320px;
      min-width: 0;
      max-width: 320px;
    `,
  ],
  disabled:    css`
    opacity: .4;
  `,
  btnRow:      [
    FLEX_ROW,
    css`
      justify-content: flex-end;
      margin: 50px 20px 20px;
    `,
  ],
  saveBtn:     [
    btnStyle.primary(COLORS.BLUE),
    css`
      min-width: 145px;
    `,
  ],
  cancelBtn:   [
    btnStyle.secondary(COLORS.BLUE),
    css`
      min-width: 145px;
    `,
  ],
};


type Props = {
  className?: string;
};


/**
 * AddDevPlanItemModal: a modal component that renders a form for adding a new development plan item.
 *
 * PureComponent: true
 * Redux Connected: false
 */
const AddDevPlanItemModal: React.FC<Props> = props => {
  const history  = useHistory();
  const dispatch = useDispatch();

  const title          = useMsgText(Str.WhatToDevelop);
  const txtPlaceholder = useMsgText(Str.EnterDevItem);
  const selPlaceholder = useMsgText(Str.ChooseCompetency);

  const dir          = useAppState(state => state.locale.textDirection);
  const competencies = useAppState(state => state.devResources || []);

  const participantId = useAppState(state => state.auth.currentUser?.personId ?? -1);
  const displayId     = useAppState(state => state.projectSummary?.displayProjectId || '');
  const projectId     = useAppState(state => state.projectSummary?.projectId ?? -1);
  const routes        = useRoutes(participantId, displayId);

  const devPlanProjectId = useAppState(state => state.projectSummary?.devPlanProjectId || projectId);

  const allowAddByCompetency = useAppState(state => !!state.projectSummary?.developmentPlanAddCompetenciesFromDevPlan);
  const allowAddManual       = useAppState(state => !!state.projectSummary?.developmentPlanAddManual);

  const [txt, setTxt]                         = useState<string | undefined>(undefined);
  const [competency, setCompetency]           = useState<number | undefined>(undefined);
  const [addByCompetency, setAddByCompetency] = useState<boolean>(allowAddByCompetency && !!competencies.length);

  const allowSubmit = useMemo(
    () => addByCompetency ? !!competencies.length && competency !== undefined && competency >= 0 : !!txt?.trim().length,
    [competency, txt, addByCompetency, competencies],
  );

  const dismiss = React.useCallback(() => history.push(routes.DevPlan), [routes, history]);

  const onChangeTxt    = React.useCallback(event => setTxt(event.target.value), [setTxt]);
  const onChangeSelect = React.useCallback(event => setCompetency(+event.target.value), [setCompetency]);

  const onCancel = useCallback(
    event => {
      event.preventDefault();
      dismiss();
    },
    [dismiss],
  );

  const onSubmit = useCallback(
    event => {
      event.preventDefault();

      dispatch(addDevPlanItemAction(participantId, devPlanProjectId, competency ?? -1, txt));

      dismiss();
    },
    [txt, competency, participantId, devPlanProjectId, dismiss, dispatch],
  );

  return (
    <Modal onDismiss={dismiss} dismissable={true} css={styles.modal} className={props.className}>
      <ModalHeader title={title} onDismiss={dismiss} />
      <form
        onReset={onCancel}
        onSubmit={onSubmit}
        css={styles.root}
      >
        <LocalDiv css={styles.formInputs}>
          {
            allowAddByCompetency && (
              <>
                <label
                  dir={dir}
                  css={[styles.label, !addByCompetency && styles.disabled]}
                  onClick={() => setAddByCompetency(true)}
                >
                  {
                    allowAddManual && (
                      <>
                        <input type='radio' css={styles.radioBtn} checked={addByCompetency} />
                        <Spacer />
                      </>
                    )
                  }
                  <select dir={dir} value={competency} onChange={onChangeSelect} css={styles.select}>
                    <option dir={dir} value={-1} hidden={true} label={selPlaceholder}>{selPlaceholder}</option>
                    {
                      competencies.map(
                        c => c.visibilityOn && (
                          <option key={c.competencyId} value={c.competencyId} label={c.name}>{c.name}</option>
                        ),
                      )
                    }
                  </select>
                </label>
              </>
            )
          }
          {
            allowAddByCompetency && allowAddManual && (
              <>
                <Spacer sizePx={15} />
                <Msg str={Str.Or} css={styles.orMsg} />
                <Spacer sizePx={15} />
              </>
            )
          }
          {
            allowAddManual && (
              <label
                dir={dir}
                css={[styles.label, addByCompetency && styles.disabled]}
                onClick={() => setAddByCompetency(false)}
              >
                {
                  allowAddByCompetency && (
                    <>
                      <input type='radio' css={styles.radioBtn} checked={!addByCompetency} />
                      <Spacer />
                    </>
                  )
                }
                <input
                  type='text'
                  value={txt}
                  onChange={onChangeTxt}
                  placeholder={txtPlaceholder}
                  css={styles.txtBox}
                />
              </label>
            )
          }
        </LocalDiv>

        {/* Submit/Cancel */}
        <LocalDiv css={styles.btnRow}>
          <LocalBtn type='reset' str={Str.Cancel} css={styles.cancelBtn} />
          <Spacer sizePx={5} />
          <LocalBtn
            type='submit'
            str={Str.AddItem}
            disabled={!allowSubmit}
            css={styles.saveBtn}
          />
        </LocalDiv>
      </form>
    </Modal>
  );
};

export default AddDevPlanItemModal;
